import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from './Toast';
import { CartContext } from '../context/CartContext';
import { useWishlistContext } from '../context/WishlistContext';
import AuthContext from '../context/AuthContext';
import { resolveAssetUrl, productPath } from '../config';
import { getDisplayPrices, shouldShowIncVatLabel } from '../utils/pricing';

const getImageUrl = (imagePath) => resolveAssetUrl(imagePath);

export default function ProductListCard({ product }) {
    const [imgError, setImgError] = useState(false);
    const [buying, setBuying] = useState(false);
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const navigate = useNavigate();
    const { addToCart } = useContext(CartContext);
    const { isAuthenticated } = useContext(AuthContext);
    const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlistContext();

    const brandName = product.brand?.name || product.brand || '';
    const pricing = getDisplayPrices(product);
    const price = pricing.displayPrice;
    const strikePrice = pricing.strikePrice;
    const discount = product.discountPercentage || product.discount || 0;
    const image = product.primaryImage ? getImageUrl(product.primaryImage) : product.image || '';
    const productLink = productPath(
        product.variantSlug
        || product.variant?.slug
        || product.variants?.find?.((v) => v?.slug)?.slug
        || product.variants?.[0]?.slug
        || product.slug
        || product._id
        || product.id
    );
    const variantCount = product.variantCount || 0;
    const isOutOfStock =
        typeof product.inStock === "boolean"
            ? !product.inStock
            : Boolean(product.forceOutOfStock);

    const selectedVariantId = product.availableVariantId || product.defaultVariantId || product.variantId;
    const isWishlisted = selectedVariantId ? isInWishlist(selectedVariantId) : false;

    const handleBuyNow = async (e) => {
        e.stopPropagation();
        const variantId = product.availableVariantId || product.defaultVariantId || product.variantId;
        if (!variantId) {
            navigate(productLink);
            return;
        }
        if (isOutOfStock) return;
        try {
            setBuying(true);
            await addToCart(variantId, 1);
            navigate('/cart');
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to buy now';
            toast.error(msg);
        } finally {
            setBuying(false);
        }
    };

    const handleCardClick = () => {
        navigate(productLink);
    };

    const handleWishlistClick = async (e) => {
        e.stopPropagation();
        if (!isAuthenticated) {
            toast.info('Please login to add items to your wishlist');
            navigate('/login');
            return;
        }
        if (!selectedVariantId) {
            toast.info('Please open the product to select a variant first');
            return;
        }
        setWishlistLoading(true);
        try {
            if (isWishlisted) {
                await removeFromWishlist(selectedVariantId);
                toast.success('Removed from wishlist');
            } else {
                await addToWishlist(selectedVariantId);
                toast.success('Added to wishlist!');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to update wishlist';
            toast.error(msg);
        } finally {
            setWishlistLoading(false);
        }
    };

    return (
        <div
            onClick={handleCardClick}
            className={`bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all group cursor-pointer flex flex-row md:flex-col h-auto md:h-full ${
                isOutOfStock ? 'opacity-60 grayscale-[30%]' : ''
            }`}
        >
            {/* Image — left on mobile, top on md+ */}
            <div className="relative w-[38%] min-w-[120px] max-w-[160px] md:w-full md:max-w-none md:min-w-0 h-auto min-h-[132px] md:h-64 bg-muted overflow-hidden shrink-0 self-stretch">
                {image && !imgError ? (
                    <img
                        src={image}
                        alt={product.name}
                        className="absolute inset-0 w-full h-full object-contain object-center p-2 md:p-0 group-hover:scale-105 transition-transform duration-300"
                        onError={() => setImgError(true)}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <i className="ri-image-line text-3xl md:text-4xl text-muted-foreground/40"></i>
                    </div>
                )}

                <div className="absolute top-2 left-2 md:top-3 md:left-3 flex flex-col gap-1.5 z-10">
                    {discount > 0 && !isOutOfStock && (
                        <span className="px-1.5 py-0.5 md:px-2 md:py-1 bg-primary text-white text-[10px] md:text-xs font-bold rounded whitespace-nowrap">
                            {discount}% OFF
                        </span>
                    )}
                    {isOutOfStock && (
                        <span className="px-1.5 py-0.5 md:px-2 md:py-1 bg-red-600 text-white text-[10px] md:text-xs font-bold rounded whitespace-nowrap">
                            Out of Stock
                        </span>
                    )}
                </div>

                <button
                    onClick={handleWishlistClick}
                    disabled={wishlistLoading}
                    className="absolute top-2 right-2 md:top-3 md:right-3 w-8 h-8 md:w-9 md:h-9 hidden md:flex items-center justify-center bg-card/95 rounded-full shadow-md hover:bg-muted transition-colors disabled:opacity-60 z-10"
                >
                    {wishlistLoading ? (
                        <i className="ri-loader-4-line animate-spin text-primary text-base"></i>
                    ) : (
                        <i className={`${isWishlisted ? 'ri-heart-fill text-primary' : 'ri-heart-line text-muted-foreground'} text-base md:text-lg`}></i>
                    )}
                </button>
            </div>

            {/* Content — right on mobile, below on md+ */}
            <div className="p-3 md:p-4 flex flex-col flex-1 min-w-0 justify-between gap-2">
                <div>
                    {brandName && (
                        <p className="text-[11px] md:text-xs text-muted-foreground mb-0.5 truncate">{brandName}</p>
                    )}
                    <h3 className="text-sm font-semibold text-foreground line-clamp-2 md:min-h-[40px]">
                        {product.name}
                    </h3>
                </div>

                <div className="mt-auto">
                    <div className="mb-2 md:mb-3">
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-base md:text-xl font-bold text-foreground">
                                KSh {price.toLocaleString()}
                            </span>
                            {strikePrice && strikePrice > price && (
                                <span className="text-xs md:text-sm text-muted-foreground line-through">
                                    KSh {strikePrice.toLocaleString()}
                                </span>
                            )}
                        </div>
                        {shouldShowIncVatLabel(product?.vatMode) && (
                            <p className="text-[10px] md:text-[11px] text-muted-foreground mt-0.5">Inc. V.A.T</p>
                        )}
                        {variantCount > 1 && (
                            <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 hidden sm:block">
                                {variantCount} variants available
                            </p>
                        )}
                        {product.emi && (
                            <p className="text-[10px] md:text-xs text-success mt-0.5">
                                <i className="ri-bank-card-line"></i> EMI from {product.emi}
                            </p>
                        )}
                    </div>

                    {product.kcbOffer && (
                        <div className="mb-2 md:mb-3">
                            <span className="inline-block px-2 py-0.5 bg-[#E8F5E9] text-success text-[10px] md:text-xs font-bold rounded whitespace-nowrap">
                                KCB Offer
                            </span>
                        </div>
                    )}

                    {/* Half wishlist + half Buy Now */}
                    <div className="flex items-stretch gap-2 md:opacity-0 md:translate-y-2 md:group-hover:opacity-100 md:group-hover:translate-y-0 transition-all duration-300">
                        <button
                            type="button"
                            onClick={handleWishlistClick}
                            disabled={wishlistLoading}
                            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                            className="flex-1 min-w-0 h-9 md:h-10 flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors disabled:opacity-60 text-xs md:text-sm font-medium"
                        >
                            {wishlistLoading ? (
                                <i className="ri-loader-4-line animate-spin text-primary text-base"></i>
                            ) : (
                                <>
                                    <i className={`${isWishlisted ? 'ri-heart-fill text-primary' : 'ri-heart-line text-muted-foreground'} text-base`}></i>
                                    <span className="truncate">{isWishlisted ? 'Saved' : 'Wishlist'}</span>
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={handleBuyNow}
                            disabled={buying || isOutOfStock}
                            className={`flex-1 min-w-0 h-9 md:h-10 px-2 rounded-lg font-medium text-xs md:text-sm transition-all whitespace-nowrap ${
                                isOutOfStock
                                    ? 'bg-muted-foreground/40 text-white cursor-not-allowed'
                                    : 'bg-primary text-white hover:brightness-110 disabled:opacity-60'
                            }`}
                        >
                            {isOutOfStock ? (
                                'Out of Stock'
                            ) : buying ? (
                                '...'
                            ) : (
                                'Buy Now'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
